import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
  input,
} from '@angular/core';
import { BoDialogComponent } from '@3ddv/software-division-components/backoffice/dialog';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCircleHelp, lucideEye, lucidePower } from '@ng-icons/lucide';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { DialogCloseReason } from 'src/app/private/layout/dialog/app-dialog.service';
import { ApiResponse } from 'src/app/private/models/api.model';
import { UtilsService } from 'src/app/private/services/utils.service';
import { User, USER_TYPES } from '../../models/users.model';
import { UsersService } from '../../services/users.service';

import { FormsModule } from '@angular/forms';

const MODAL_ACTION_ENUM = {
  edit: 'edit',
  delete: 'delete',
  create: 'create',
} as const;

export type ModalAction =
  (typeof MODAL_ACTION_ENUM)[keyof typeof MODAL_ACTION_ENUM];

@Component({
  selector: 'app-modal-user',
  templateUrl: './modal-user.component.html',
  styleUrls: ['./modal-user.component.css'],
  imports: [FormsModule, BoDialogComponent, ButtonComponent, NgIcon],
  viewProviders: [provideIcons({ lucideCircleHelp, lucideEye, lucidePower })],
})
export class ModalUserComponent implements OnInit, OnDestroy {
  private dialogRef = inject<BrnDialogRef<DialogCloseReason>>(BrnDialogRef);
  private usersService = inject(UsersService);
  private utils = inject(UtilsService);
  private toastr = inject(ToastrService);

  readonly action = input.required<ModalAction>();
  @Input() user: User | null = null;

  public actionsEnum = MODAL_ACTION_ENUM;
  private subscriptions: Array<Subscription>;
  public hashValidator: Record<keyof User, boolean> = {
    id: false,
    username: false,
    password: false,
    email: false,
    isSuperadmin: false,
    type: false,
    pvPatronId: false,
    createdAt: false,
    updatedAt: false,
  };
  public userTypes = Object.values(USER_TYPES).sort();
  public passwordInputType: 'password' | 'text' = 'password';
  public confirmPassword = { value: '', hasError: false };

  constructor() {
    this.subscriptions = [];
  }

  ngOnInit(): void {
    const defaultUser: User = {
      id: 0,
      username: '',
      password: '',
      email: '',
      type: USER_TYPES.ADMIN,
      isSuperadmin: false,
      pvPatronId: '',
      createdAt: '',
      updatedAt: '',
    };
    this.user = this.user || defaultUser;

    if (this.action() === MODAL_ACTION_ENUM.edit) {
      this.user.password = '';
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  public togglePasswordInputType() {
    this.passwordInputType =
      this.passwordInputType === 'password' ? 'text' : 'password';
  }

  public get title(): string {
    const action = this.action();
    const title = `${action.charAt(0).toUpperCase()}${action.slice(1)} User`;

    return this.user?.id ? `${title} - Id ${this.user.id}` : title;
  }

  public onDismiss(reason: DialogCloseReason) {
    this.dialogRef.close(reason);
  }

  public onConfirm() {
    const hasErrors = this.validateInputData();

    if (hasErrors) {
      return;
    }

    const saveOptions: Record<
      ModalAction,
      () => Observable<ApiResponse<Pick<User, 'id'>>>
    > = {
      edit: () => {
        const { password, ...user } = this.user;
        return this.usersService.update$(password ? this.user : user);
      },
      delete: () => this.usersService.delete$(this.user),
      create: () => this.usersService.create$(this.user),
    };

    const saveFunction = saveOptions[this.action()];

    const saveSub = saveFunction().subscribe({
      next: () => {
        this.onDismiss('success');
      },
      error: error => {
        this.toastr.error(
          error?.error?.message,
          `Error on ${this.action()} user`
        );
        console.log(`Error on ${this.action()} user`, error);
      },
    });

    this.subscriptions.push(saveSub);
  }

  private validateInputData(): boolean {
    let hasErrors = false;
    Object.keys(this.hashValidator).forEach(value => {
      this.hashValidator[value] = false;
    });

    if (this.utils.isEmpty(this.user.username)) {
      this.hashValidator.username = true;
      hasErrors = true;
    }

    if (this.action() === MODAL_ACTION_ENUM.create) {
      if (!this.utils.isValidPassword(this.user.password)) {
        this.hashValidator.password = true;
        hasErrors = true;
      }
    }

    if (this.action() === MODAL_ACTION_ENUM.edit && this.user.password) {
      if (!this.utils.isValidPassword(this.user.password)) {
        this.hashValidator.password = true;
        hasErrors = true;
      }

      if (this.user.password !== this.confirmPassword.value) {
        this.confirmPassword.hasError = true;
        hasErrors = true;
      }
    }
    if (!this.utils.isValidEmail(this.user.email)) {
      this.hashValidator.email = true;
      hasErrors = true;
    }

    const isInvalidUserType = Object.values(USER_TYPES).every(
      v => v !== this.user.type
    );
    if (isInvalidUserType) {
      this.hashValidator.type = true;
      hasErrors = true;
    }

    return hasErrors;
  }
}
